import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Table, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import { dollarsToCents, formatCents } from '../utils/money';
import './Dashboard.css';
import './AdminTheme.css';

/** Format a Date as YYYY-MM-DD (matches the app's <input type="date"> values). */
const toISODate = (d) => d.toISOString().split('T')[0];

/** Default portal date window: the past 3 months (front-end default only). */
function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return { start: toISODate(start), end: toISODate(end) };
}

/**
 * InvestorDashboard — standalone portal for the investor role.
 *
 * Investors see, for their assigned vehicles only (across any store):
 *   1. Transactions & payments: transaction date and how much has been
 *      paid on each rental — intentionally without any customer info
 *   2. Car costs: what has been spent maintaining/fixing each vehicle
 */
export default function InvestorDashboard() {
  const { user, apiCall, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vehicleFilter, setVehicleFilter] = useState('');

  // Transaction-date window. Defaults to the past 3 months on the front end;
  // the backend applies no default (empty bound => no filter on that side).
  const initialRange = useMemo(defaultDateRange, []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const qs = params.toString();
      const res = await apiCall(`${API_BASE_URL}/investor/portal${qs ? `?${qs}` : ''}`);
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load your portfolio');
    } finally {
      setLoading(false);
    }
  }, [apiCall, startDate, endDate]);

  useEffect(() => {
    if (apiCall) loadData();
  }, [apiCall, loadData]);

  const vehicles = data?.vehicles || [];
  const vehicleById = useMemo(() => {
    const map = {};
    for (const v of vehicles) map[v.id] = v;
    return map;
  }, [vehicles]);

  const vehicleLabel = (vehicleId) => {
    const v = vehicleById[vehicleId];
    if (!v) return `Vehicle #${vehicleId}`;
    return `${v.year} ${v.make} ${v.model} · ${v.license_plate}`;
  };

  const transactions = useMemo(() => {
    const rows = data?.transactions || [];
    return vehicleFilter ? rows.filter(t => String(t.vehicle_id) === vehicleFilter) : rows;
  }, [data, vehicleFilter]);

  const costs = useMemo(() => {
    const rows = data?.costs || [];
    return vehicleFilter ? rows.filter(c => String(c.vehicle_id) === vehicleFilter) : rows;
  }, [data, vehicleFilter]);

  // Totals (integer cents; null-safe per money convention)
  const totals = useMemo(() => {
    let billed = 0, collected = 0, spent = 0;
    for (const t of transactions) {
      billed += (dollarsToCents(t.amount) ?? 0) + (dollarsToCents(t.tax_amount) ?? 0);
      collected += dollarsToCents(t.paid_amount) ?? 0;
    }
    for (const c of costs) {
      spent += dollarsToCents(c.amount) ?? 0;
    }
    return { billed, collected, spent };
  }, [transactions, costs]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).split('T')[0] || '—';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const paymentStatus = (t) => {
    const owedCents = (dollarsToCents(t.amount) ?? 0) + (dollarsToCents(t.tax_amount) ?? 0);
    const paidCents = dollarsToCents(t.paid_amount) ?? 0;
    if (paidCents >= owedCents && owedCents > 0) return <Badge bg="success">Paid</Badge>;
    if (paidCents > 0) return <Badge bg="warning" text="dark">Partial</Badge>;
    return <Badge bg="danger">Not paid</Badge>;
  };

  const costTypeLabel = (type) => (type || 'other').replace(/_/g, ' ');

  return (
    <div className="tg-dash tg-admin">
      <header className="tg-admin-header">
        <div className="tg-topbar">
          <div className="tg-topbar-row">
            <div className="tg-brand">
              <span className="tg-brand-mark">T</span>
              Tegrat Auto <span className="tg-brand-role">Investor Portal</span>
            </div>
            <div className="tg-topbar-right">
              <span className="tg-user-chip">
                {user?.name || user?.email} · {user?.role}
              </span>
              <button type="button" className="tg-btn sm" onClick={logout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <Container fluid className="py-4">
        {error && <Alert variant="danger">{error}</Alert>}

        {loading && !data ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <Row className="mb-4 g-3">
              <Col sm={6} lg={3}>
                <Card className="h-100 tg-admin-stat">
                  <Card.Body>
                    <div className="text-muted small">Your Vehicles</div>
                    <h3 className="mb-0">{vehicles.length}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="h-100 tg-admin-stat">
                  <Card.Body>
                    <div className="text-muted small">Total Billed</div>
                    <h3 className="mb-0">{formatCents(totals.billed)}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="h-100 tg-admin-stat">
                  <Card.Body>
                    <div className="text-muted small">Total Collected</div>
                    <h3 className="mb-0 text-success">{formatCents(totals.collected)}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="h-100 tg-admin-stat">
                  <Card.Body>
                    <div className="text-muted small">Vehicle Costs</div>
                    <h3 className="mb-0 text-danger">{formatCents(totals.spent)}</h3>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-3 g-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="small text-muted mb-1">Vehicle</Form.Label>
                <Form.Select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)}>
                  <option value="">All vehicles ({vehicles.length})</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.year} {v.make} {v.model} · {v.license_plate} ({v.store_name || 'No store'})
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="small text-muted mb-1">From (transaction date)</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="small text-muted mb-1">To</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              {loading && (
                <Col xs="auto" className="pb-2">
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Updating…</span>
                  </Spinner>
                </Col>
              )}
            </Row>

            {vehicles.length === 0 && (
              <Alert variant="info">
                No vehicles have been assigned to you yet. Please contact your administrator.
              </Alert>
            )}

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Transactions &amp; Payments</h5>
              </Card.Header>
              <Card.Body className="p-0">
                {transactions.length === 0 ? (
                  <div className="text-center py-4 text-muted">No transactions yet.</div>
                ) : (
                  <Table striped hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Transaction Date</th>
                        <th>Vehicle</th>
                        <th>Rental Period</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">Paid</th>
                        <th>Last Payment</th>
                        <th>Payment Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(t => {
                        const owedCents = (dollarsToCents(t.amount) ?? 0) + (dollarsToCents(t.tax_amount) ?? 0);
                        return (
                          <tr key={t.id}>
                            <td>{formatDate(t.transaction_date)}</td>
                            <td>{vehicleLabel(t.vehicle_id)}</td>
                            <td>
                              {t.start_date || t.end_date
                                ? `${formatDate(t.start_date)} → ${formatDate(t.end_date)}`
                                : '—'}
                            </td>
                            <td className="text-end">{formatCents(owedCents)}</td>
                            <td className="text-end">{formatCents(dollarsToCents(t.paid_amount) ?? 0)}</td>
                            <td>{formatDate(t.last_payment_date)}</td>
                            <td>{paymentStatus(t)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Vehicle Costs</h5>
              </Card.Header>
              <Card.Body className="p-0">
                {costs.length === 0 ? (
                  <div className="text-center py-4 text-muted">No costs recorded yet.</div>
                ) : (
                  <Table striped hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.map(c => (
                        <tr key={c.id}>
                          <td>{formatDate(c.cost_date)}</td>
                          <td>{vehicleLabel(c.vehicle_id)}</td>
                          <td className="text-capitalize">{costTypeLabel(c.type)}</td>
                          <td>{c.description}</td>
                          <td className="text-end">{formatCents(dollarsToCents(c.amount) ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
}
